import * as React from "react";
import { makeStyles, createStyles, Theme, Typography } from "@material-ui/core";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import ReactMarkdown from "react-markdown";
import Section from "../Section";
import {
  SECTION_WHITE,
  TEXT_BODY,
  TEXT_HEADING,
  SECTION_PADDING_Y,
} from "../../styles/homepageSections";

export type PlatformOverviewData = {
  subheadline?: string | null;
  headline?: string | null;
  description?: string | null;
  diagramAlt?: string | null;
  diagram?: {
    localFile?: {
      childImageSharp?: {
        gatsbyImageData: IGatsbyImageData;
      };
      publicURL?: string;
    };
  } | null;
};

const hasText = (value?: string | null) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    section: {
      padding: SECTION_PADDING_Y.mobile,
      [t.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y.desktop,
      },
    },
    inner: {
      textAlign: "center",
      maxWidth: 900,
      margin: "0 auto 3rem",
      [t.breakpoints.up("md")]: {
        marginBottom: "4rem",
      },
    },
    subheadline: {
      color: t.palette.primary.main,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      marginBottom: 12,
      [t.breakpoints.up("md")]: {
        fontSize: 13,
        marginBottom: 16,
      },
    },
    headline: {
      color: TEXT_HEADING,
      fontWeight: 600,
      fontSize: 32,
      lineHeight: 1.25,
      margin: 0,
      padding: "0 0 2rem !important",
      [t.breakpoints.up("md")]: {
        fontSize: 44,
        lineHeight: 1.2,
      },
    },
    headlineAfterSubheadline: {
      paddingTop: "0 !important",
      marginTop: 0,
    },
    description: {
      color: TEXT_BODY,
      fontSize: 18,
      lineHeight: 1.55,
      maxWidth: 800,
      margin: "0 auto",
      "& p": {
        margin: "0 0 1em",
      },
      "& p:last-child": {
        marginBottom: 0,
      },
      "& strong": {
        color: TEXT_HEADING,
        fontWeight: 600,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 20,
        lineHeight: 1.6,
      },
    },
    diagramWrapper: {
      width: "100%",
      maxWidth: 1200,
      margin: "0 auto",
      lineHeight: 0,
    },
    diagram: {
      width: "100%",
      height: "auto",
      display: "block",
    },
  })
);

const PlatformOverviewSection: React.FC<{ data?: PlatformOverviewData | null }> = ({
  data,
}) => {
  const classes = useStyles();

  if (!data) {
    return null;
  }

  const gatsbyImage = data.diagram?.localFile?.childImageSharp?.gatsbyImageData;
  const fallbackUrl = data.diagram?.localFile?.publicURL;
  const hasImage = Boolean(gatsbyImage || fallbackUrl);
  const hasCopy =
    hasText(data.subheadline) ||
    hasText(data.headline) ||
    hasText(data.description);

  if (!hasCopy && !hasImage) {
    return null;
  }

  const alt =
    data.diagramAlt ||
    (hasText(data.headline) ? String(data.headline) : "Platform overview diagram");

  return (
    <Section color={SECTION_WHITE} className={classes.section}>
      {hasCopy && (
        <div className={classes.inner}>
          {hasText(data.subheadline) && (
            <Typography component="p" className={classes.subheadline}>
              {data.subheadline}
            </Typography>
          )}
          {hasText(data.headline) && (
            <Typography
              variant="h2"
              component="h2"
              className={[
                classes.headline,
                hasText(data.subheadline) && classes.headlineAfterSubheadline,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {data.headline}
            </Typography>
          )}
          {hasText(data.description) && (
            <div className={classes.description}>
              <ReactMarkdown
                linkTarget="_blank"
                components={{
                  p: ({ children }) => <p>{children}</p>,
                  strong: ({ children }) => <strong>{children}</strong>,
                }}
              >
                {data.description!}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {hasImage && (
        <div className={classes.diagramWrapper}>
          {gatsbyImage ? (
            <GatsbyImage image={gatsbyImage} alt={alt} className={classes.diagram} />
          ) : (
            <img src={fallbackUrl} alt={alt} className={classes.diagram} />
          )}
        </div>
      )}
    </Section>
  );
};

export default PlatformOverviewSection;
